import type { Logger } from "@expert/logging";
import type { Partner, SendExpertAssistMessage } from "@expert/shared-types";
import type { TMessage } from "@simplrsupport/headless-chat-web";
import { getMessageDataType, MESSAGE_DATA_TYPES, useChatSessionStateStore } from "@simplrsupport/headless-chat-web";
import { useMemo } from "react";
import { GuideCard, GuideSelectionCard } from "../components";
import type { CloseGuideSource } from "../types";
import { getGuideCardPropsFromTextMessage, getPropsFromGuideSelectionMessage } from "./logicBuilderResolvers";

export interface GuidedFlowMessageMapperProps {
    areGuidesAvailable: boolean;
    callSid?: string;
    clientChannelId?: string;
    latestMessage: TMessage | undefined;
    logger: Logger;
    partner: Partner;
    onGuideClose: (source: CloseGuideSource) => void;
    opened: boolean;
    requiresTopPadding: boolean;
    sendMessage: SendExpertAssistMessage;
    sessionId: string;
    ssoAccessToken: string;
    taskMdn: string;
}

export const GuidedFlowMessageMapper = ({
    areGuidesAvailable,
    callSid,
    clientChannelId,
    latestMessage,
    logger: loggerProp,
    partner,
    onGuideClose,
    opened,
    requiresTopPadding,
    sendMessage,
    sessionId,
    ssoAccessToken,
    taskMdn,
}: GuidedFlowMessageMapperProps) => {
    // moreTicketInfo is the logic builder's way of storing metadata about the session
    const { platformTicketId } = useChatSessionStateStore();

    const logger = useMemo(
        () => loggerProp.child({ module: "GuidedFlowMessageMapper", platformTicketId }),
        [loggerProp, platformTicketId],
    );

    if (!latestMessage) return null;
    if (!areGuidesAvailable) return null;

    const messageType = getMessageDataType(latestMessage) as MESSAGE_DATA_TYPES;
    switch (messageType) {
        case MESSAGE_DATA_TYPES.CUSTOM: {
            const { currentStepId } = getPropsFromGuideSelectionMessage(latestMessage);
            return (
                <GuideSelectionCard
                    callSid={callSid}
                    logger={logger}
                    onGuideClose={onGuideClose}
                    opened={opened}
                    stepId={currentStepId}
                />
            );
        }
        case MESSAGE_DATA_TYPES.TEXT: {
            const {
                actions,
                alternateText,
                currentStep,
                dropdownInput,
                hasPreviousSteps,
                progress,
                prompt,
                tag,
                textInput,
                variant,
            } = getGuideCardPropsFromTextMessage(latestMessage);

            return (
                <GuideCard
                    alternateText={alternateText}
                    actions={actions}
                    callSid={callSid}
                    clientChannelId={clientChannelId}
                    currentStep={currentStep}
                    dropdownInput={dropdownInput}
                    hasPreviousSteps={hasPreviousSteps}
                    logger={logger}
                    onGuideClose={onGuideClose}
                    opened={opened}
                    partner={partner}
                    progress={progress}
                    prompt={prompt}
                    requiresTopPadding={requiresTopPadding}
                    sendMessage={sendMessage}
                    sessionId={sessionId}
                    ssoAccessToken={ssoAccessToken}
                    tag={tag}
                    taskMdn={taskMdn}
                    textInput={textInput}
                    variant={variant}
                />
            );
        }
        default:
            logger.info({ latestMessage, messageType }, "Solve guide | Unsupported message type.");
            return null;
    }
};
